import { useMutation } from '@apollo/client';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { InsertObligationChangeAttestationOneMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertObligationChangeAttestationOneDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { InsertObligationChangeAttestationTRPCInput } from './useInsertObligationChangeAttestationTRPC';
import { useInsertObligationChangeAttestationTRPC } from './useInsertObligationChangeAttestationTRPC';

export const useInsertObligationChangeAttestation = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const { user } = useRisksmartUser();

  // GraphQL mutation
  const [insertAttestationGraphQL, graphqlState] = useMutation(
    InsertObligationChangeAttestationOneDocument
  );

  // tRPC mutation
  const trpcMutation = useInsertObligationChangeAttestationTRPC();

  const insertObligationChangeAttestation = async (
    variables: InsertObligationChangeAttestationTRPCInput
  ): Promise<InsertObligationChangeAttestationOneMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertObligationChangeAttestation(variables);
    }

    // The legacy Hasura action needs UserId, derived from the authenticated
    // user (the server derives it on the tRPC path).
    const result = await insertAttestationGraphQL({
      variables: {
        object: {
          ObligationChangeId: variables.ObligationChangeId,
          UserId: user!.userId,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to insert obligation change attestation');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertObligationChangeAttestation,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertObligationChangeAttestation,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
